import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import { maskPhone } from '../utils/formatters';

interface RecruiterDetail {
  id: number;
  email: string;
  name?: string;
  confirmed_at?: string;
  created_at: string;
  position?: string;
  telephone?: string;
}

interface Props {
  recruiterId: number;
  onClose: () => void;
}

const RecruiterDetailModal: React.FC<Props> = ({ recruiterId, onClose }) => {
  const [recruiter, setRecruiter] = useState<RecruiterDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecruiter();
  }, [recruiterId]);

  const fetchRecruiter = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/recruiter-list/${recruiterId}`);
      setRecruiter(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do recrutador');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Detalhes do(a) Recruiter</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        {loading ? (
          <div style={styles.loading}>Carregando...</div>
        ) : recruiter ? (
          <div style={styles.content}>
            <div style={styles.detailRow}>
              <span style={styles.label}>Id:</span>
              <span style={styles.value}>{recruiter.id}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Nome:</span>
              <span style={styles.value}>{recruiter.name || '-'}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>E-mail:</span>
              <span style={styles.value}>{recruiter.email}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Posição:</span>
              <span style={styles.value}>{recruiter.position || '-'}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Telefone:</span>
              <span style={styles.value}>{recruiter.telephone ? maskPhone(recruiter.telephone) : '-'}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Confirmado:</span>
              <span style={styles.value}>
                <span style={recruiter.confirmed_at ? styles.yesBadge : styles.noBadge}>
                  {recruiter.confirmed_at ? 'Sim' : 'Não'}
                </span>
              </span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Confirmed At:</span>
              <span style={styles.value}>
                {recruiter.confirmed_at ? (
                  <>
                    {new Date(recruiter.confirmed_at).toLocaleDateString('pt-BR')} {new Date(recruiter.confirmed_at).toLocaleTimeString('pt-BR')}
                  </>
                ) : '-'}
              </span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Created At:</span>
              <span style={styles.value}>
                {new Date(recruiter.created_at).toLocaleDateString('pt-BR')} {new Date(recruiter.created_at).toLocaleTimeString('pt-BR')}
              </span>
            </div>
          </div>
        ) : (
          <div style={styles.empty}>Recrutador não encontrado</div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  content: {
    padding: '1.5rem',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center' as const,
  },
  empty: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: colors.textLight,
  },
  detailRow: {
    display: 'flex',
    padding: '0.75rem 0',
    borderBottom: `1px solid ${colors.border}`,
  },
  label: {
    fontWeight: 'bold',
    minWidth: '150px',
    color: colors.text,
  },
  value: {
    flex: 1,
    color: colors.text,
  },
  yesBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  noBadge: {
    backgroundColor: colors.error,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
};

export default RecruiterDetailModal;
